import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import * as api from "../services/api";
import { useAuth } from "./AuthContext";

const UserProfileContext = createContext(null);

export function UserProfileProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [equipmentPresets, setEquipmentPresets] = useState([]);
  const [locationPresets, setLocationPresets] = useState([]);
  const [performanceCapabilities, setPerformanceCapabilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedUserData, setHasLoadedUserData] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Fetch user data when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setProfile(null);
      setSessions([]);
      setEquipmentPresets([]);
      setLocationPresets([]);
      setPerformanceCapabilities([]);
      setHasLoadedUserData(false);
      return;
    }

    const fetchUserData = async () => {
      setLoading(true);
      setHasLoadedUserData(false);
      setError(null);

      try {
        const [
          profileData,
          sessionsData,
          equipmentData,
          locationData,
          capabilityData,
        ] = await Promise.all([
          api.getProfile().catch(() => null),
          api.getSessions().catch(() => []),
          api.getEquipmentPresets().catch(() => []),
          api.getLocationPresets().catch(() => []),
          api.getPerformanceCapabilities().catch(() => []),
        ]);

        setProfile(profileData);
        setSessions(sessionsData);
        setEquipmentPresets(equipmentData);
        setLocationPresets(locationData);
        setPerformanceCapabilities(capabilityData);
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
        setHasLoadedUserData(true);
      }
    };

    fetchUserData();
  }, [isAuthenticated, user?.id]);

  // Profile operations
  const updateProfile = useCallback(async (profileData) => {
    const updated = await api.updateProfile(profileData);
    setProfile(updated);
    return updated;
  }, []);

  const syncProfile = useCallback(async (localProfile) => {
    setSyncing(true);
    try {
      const updated = await api.syncProfile(localProfile);
      setProfile(updated);
      return updated;
    } finally {
      setSyncing(false);
    }
  }, []);

  // Session operations
  const addSession = useCallback(async (sessionData) => {
    const session = await api.createSession(sessionData);
    setSessions((prev) => [session, ...prev]);
    return session;
  }, []);

  const updateSession = useCallback(async (sessionId, sessionData) => {
    const session = await api.updateSession(sessionId, sessionData);
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? session : s)));
    return session;
  }, []);

  const deleteSession = useCallback(async (sessionId) => {
    await api.deleteSession(sessionId);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }, []);

  const syncSessions = useCallback(async (localSessions) => {
    setSyncing(true);
    try {
      const result = await api.syncSessions(localSessions);
      setSessions(result.sessions);
      return result;
    } finally {
      setSyncing(false);
    }
  }, []);

  // Equipment presets
  const addEquipmentPreset = useCallback(async (preset) => {
    const result = await api.createEquipmentPreset(preset);
    setEquipmentPresets((prev) => [...prev, result]);
    return result;
  }, []);

  const updateEquipmentPreset = useCallback(async (presetId, preset) => {
    const result = await api.updateEquipmentPreset(presetId, preset);
    setEquipmentPresets((prev) =>
      prev.map((p) => (p.id === presetId ? result : p)),
    );
    return result;
  }, []);

  const deleteEquipmentPreset = useCallback(async (presetId) => {
    await api.deleteEquipmentPreset(presetId);
    setEquipmentPresets((prev) => prev.filter((p) => p.id !== presetId));
  }, []);

  // Location presets
  const addLocationPreset = useCallback(async (preset) => {
    const result = await api.createLocationPreset(preset);
    setLocationPresets((prev) => [...prev, result]);
    return result;
  }, []);

  const updateLocationPreset = useCallback(async (presetId, preset) => {
    const result = await api.updateLocationPreset(presetId, preset);
    setLocationPresets((prev) =>
      prev.map((p) => (p.id === presetId ? result : p)),
    );
    return result;
  }, []);

  const deleteLocationPreset = useCallback(async (presetId) => {
    await api.deleteLocationPreset(presetId);
    setLocationPresets((prev) => prev.filter((p) => p.id !== presetId));
  }, []);

  // Performance capabilities
  const addPerformanceCapability = useCallback(async (capability) => {
    const result = await api.createPerformanceCapability(capability);
    setPerformanceCapabilities((prev) => [...prev, result]);
    return result;
  }, []);

  const updatePerformanceCapability = useCallback(async (id, capability) => {
    const result = await api.updatePerformanceCapability(id, capability);
    setPerformanceCapabilities((prev) =>
      prev.map((c) => (c.id === id ? result : c)),
    );
    return result;
  }, []);

  const deletePerformanceCapability = useCallback(async (id) => {
    await api.deletePerformanceCapability(id);
    setPerformanceCapabilities((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const value = {
    // Profile
    profile,
    loading,
    hasLoadedUserData,
    syncing,
    error,
    updateProfile,
    syncProfile,

    // Sessions
    sessions,
    addSession,
    updateSession,
    deleteSession,
    syncSessions,

    // Equipment Presets
    equipmentPresets,
    addEquipmentPreset,
    updateEquipmentPreset,
    deleteEquipmentPreset,

    // Location Presets
    locationPresets,
    addLocationPreset,
    updateLocationPreset,
    deleteLocationPreset,

    // Performance Capabilities
    performanceCapabilities,
    addPerformanceCapability,
    updatePerformanceCapability,
    deletePerformanceCapability,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error("useUserProfile must be used within a UserProfileProvider");
  }
  return context;
}

export default UserProfileContext;
